/**
 * Keep a `position: fixed` popup inside the window: when it is shown and
 * whenever it changes size (a menu item that expands in place), a popup
 * whose bottom would fall past the window's edge is moved up until it fits.
 * A window resized while the popup is open is not followed.
 *
 * Returns a ref callback for the popup element. The popup's `top` stays
 * whatever its owner set and its transform stays its animation's; the shift
 * goes into `margin-top`, which neither touches.
 */
import { useCallback, useRef } from 'react';

const MARGIN = 8;

export function useStayInViewport<T extends HTMLElement>() {
  const observer = useRef<ResizeObserver | null>(null);
  return useCallback((el: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    el.style.marginTop = '';
    const style = getComputedStyle(el);
    const baseMargin = parseFloat(style.marginTop) || 0;
    const fit = () => {
      const top = parseFloat(getComputedStyle(el).top) || 0;
      const over = top + baseMargin + el.offsetHeight - (window.innerHeight - MARGIN);
      const shift = over > 0 ? Math.min(over, Math.max(0, top + baseMargin - MARGIN)) : 0;
      el.style.marginTop = shift ? `${baseMargin - shift}px` : '';
    };
    fit();
    observer.current = new ResizeObserver(fit);
    observer.current.observe(el);
  }, []);
}

export default useStayInViewport;
